import { axiosInstance } from '@/utils/libs/axios';
import { useMutation, useQuery, type UseQueryOptions } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import { z } from 'zod';

interface IProjectItem {
  id: string;
  title: string;
}

export const projectSchema = z.object({
  id: z.string().optional(),
  title: z.string().max(255),
  test_case_count: z.number().optional(),
});

export type IProjectSchema = z.infer<typeof projectSchema>;

export const ProjectService = {
  useGet: (
    id?: string,
    options?: Partial<UseQueryOptions<IProjectSchema, AxiosError>>,
  ) =>
    useQuery<IProjectSchema, AxiosError>({
      queryKey: ['project', id],
      queryFn: async () => {
        if (!id) {
          return {};
        }

        const res = await axiosInstance.get(`/api/crud/project/${id}`);
        return res.data.data;
      },
      retry: false,
      ...options,
    }),

  useCreate: () =>
    useMutation({
      mutationFn: async (data: IProjectSchema) => {
        const res = await axiosInstance.post('/api/crud/project', data);

        return res.data;
      },
    }),

  useUpdate: () =>
    useMutation({
      mutationFn: async (data: IProjectSchema) => {
        const res = await axiosInstance.post(
          `/api/crud/project/${data.id}`,
          data,
        );

        return res.data;
      },
    }),

  useExport: () =>
    useMutation({
      mutationFn: async ({
        filters,
        sort,
        orderBy,
      }: {
        filters?: { [x: string]: any };
        sort?: string;
        orderBy?: 'asc' | 'desc';
      }) => {
        // ExtendedTableLowCode stores filter values already encodeURIComponent()'d
        // (the datatable inserts them into the URL by string concatenation).
        // Axios re-encodes params, so passing the pre-encoded values verbatim
        // would double-encode them (e.g. "%2C" -> "%252C") and the backend
        // would silently drop the filter. Decode once, then let axios encode.
        const filterParam = Object.fromEntries(
          Object.entries(filters ?? {}).map(([key, value]) => [
            `filter[${key}]`,
            typeof value === 'string' ? decodeURIComponent(value) : value,
          ]),
        );

        const response = await axiosInstance.get('/api/crud/project/export', {
          params: { ...filterParam, sort, asc: orderBy == 'asc' },
          responseType: 'blob',
        });

        return response.data;
      },
    }),

  useDelete: () =>
    useMutation({
      mutationFn: async (id: string) => {
        const response = await axiosInstance.delete(`/api/crud/project/${id}`);

        return response.data;
      },
    }),

  useGetProjectOption: () =>
    useQuery({
      queryKey: ['project-options'],
      queryFn: async () => {
        const res = await axiosInstance.get(`/api/crud/project?show_all=true`);

        return res.data.data.content.map(
          (item: IProjectItem) => ({
            value: item.id,
            label: item.title,
          }),
        );
      },
    }),
};
